import { Box, Typography } from '@mui/material';
import moment from 'moment';
import ErrorIcon from '@mui/icons-material/Error';

import { FHTooltip } from '../../base';
import StatusIconRenderer from '../StatusIconRenderer';
import { formatEntityId, history } from '../../../helpers';
import { getCalendarEntityPrefix, getTimeLabel, isDocumentNotConcluded } from './utils';
import { calendarCardStyles, TIME_UNIT_HEIGHT } from './constants';
import { ServiceCodes } from '../billings/claims/constants';
import { SupervisionType } from '../../../constants';
import {
  FORTA_GRAY4,
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY3,
  FORTA_WHITE,
} from '../../../utils/colors';

import type {
  IBtSupervision,
  IClientAssessment,
  IClientSupervision,
  IParentSupervision,
  ISessionMinified,
} from '../../../utils/types';

type Resource =
  ISessionMinified | IBtSupervision | IClientSupervision | IClientAssessment | IParentSupervision;

interface ICalendarCardProps {
  left?: number;
  width?: string;
  resource: Resource;
  top?: number;
  units: number;
  type: SupervisionType | 'session';
}

const CustomTooltip = (
  resource: Resource,
  type: SupervisionType | 'session',
  showNotConcludedAlert: boolean,
  onIdClick: () => void,
) => {
  const { status, client, date_start } = resource;
  const { name, color } = calendarCardStyles[type] || {};
  const showBTSection = type === SupervisionType.Bt;
  const btDetails = 'bt_provider' in resource ? resource.bt_provider : undefined;
  return (
    <Box
      data-testid="hdCalendarTooltip"
      sx={{ borderRadius: '4px', border: `1px solid ${FORTA_GRAY3}`, display: 'flex', p: 0.5 }}
    >
      <Box
        sx={{
          width: '5px',
          backgroundColor: color,
          borderRadius: '8px',
        }}
      />
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <StatusIconRenderer
              iconHeight={18}
              iconWidth={18}
              procedureCode={ServiceCodes.SessionProcedureCode}
              status={status}
            />
            <Typography ml={0.5} fontSize={20}>
              {name}
            </Typography>
          </Box>
          <Typography
            color={FORTA_BLUE.primary}
            sx={{ fontSize: 14, ml: 3, cursor: 'pointer', textDecoration: 'underline' }}
            onClick={onIdClick}
          >
            {formatEntityId(String(resource.id), getCalendarEntityPrefix(type))}
          </Typography>
        </Box>
        {showNotConcludedAlert && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ErrorIcon sx={{ color: FORTA_ERROR, width: '18px', height: '18px', mt: 2 }} />
            <Typography ml={1} mt={2}>
              Not concluded
            </Typography>
          </Box>
        )}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ mt: 1 }}>
            <Typography fontSize={10} width={35} color={FORTA_GRAY4}>
              Client
            </Typography>
            <Typography>{`${client?.first_name} ${client?.last_name} (ID: ${client?.id})`}</Typography>
          </Box>
          {showBTSection && (
            <Box sx={{ alignItems: 'center', mt: 1 }}>
              <Typography fontSize={10} width={35} color={FORTA_GRAY4}>
                BT
              </Typography>
              <Typography>
                {btDetails ? `${btDetails?.first_name} ${btDetails?.last_name}` : '--'}
              </Typography>
            </Box>
          )}
          <Box sx={{ mt: 1 }}>
            <Typography fontSize={10} width={35} color={FORTA_GRAY4}>
              Date
            </Typography>
            <Typography color={FORTA_GRAY4}>{moment(date_start).format('dddd MMMM Do')}</Typography>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Typography fontSize={10} width={35} color={FORTA_GRAY4}>
              Time
            </Typography>
            <Typography color={FORTA_GRAY4}>{getTimeLabel(resource)}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

function CalendarCard({
  resource,
  units,
  type,
  top = 0,
  left = 0,
  width = '100%',
}: ICalendarCardProps) {
  const { client, status, id } = resource;
  const { name, color } = calendarCardStyles[type] || {};
  const height = TIME_UNIT_HEIGHT * units;

  const reduceTextSize = units < 3;
  const showNotConcludedAlert = isDocumentNotConcluded(
    type,
    status,
  );

  const showName = height > 12;
  const showTimeLabel = height > 60;
  const showTypeLabel = height > 38;

  const handleNavigation = () => {
    if (type === SupervisionType.Assessment) {
      history.navigate(`/assessment-note/${id}`);
    } else if (type === SupervisionType.Bt) {
      history.navigate(`bt-supervisions/${id}`);
    } else if (type === SupervisionType.Client) {
      history.navigate(`/client-supervisions/${id}`);
    } else if (type === SupervisionType.Parent) {
      history.navigate(`/parent-supervisions/${id}`);
    } else if (type === 'session') {
      history.navigate(`/sessions/${id}`);
    }
  };

  return (
    <FHTooltip
      sx={{ inset: 'auto auto -6px 0px', padding: '0px !important' }}
      title={CustomTooltip(resource, type, showNotConcludedAlert, handleNavigation)}
      placement="top-start"
      maxwidth={360}
      tooltipBorderRadius="8px"
      tooltipbackground={FORTA_WHITE}
      tooltipBoxShadow="0px 5px 5px rgba(0, 0, 0, 0.2)"
    >
      <Box
        data-testid="calendar-card"
        sx={{
          top,
          left,
          position: 'absolute',
          width,
          borderTop: `1px solid ${FORTA_GRAY3}`,
          borderBottom: `1px solid ${FORTA_GRAY3}`,
          borderLeft: `4px solid ${color}`,
          borderRight: width !== '100%' ? `1px solid ${FORTA_GRAY3}` : 'none',
          backgroundColor: FORTA_BLUE.light3,
          height,
          overflow: 'hidden',
          display: 'flex',
          ':hover': {
            cursor: 'pointer',
            boxShadow: '0 4px 4px rgba(0, 0, 0, 0.1)',
            opacity: 0.9,
          },
        }}
        onClick={handleNavigation}
      >
        <Box sx={{ mx: 1, flexGrow: 1 }}>
          {showTypeLabel && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography fontSize={10} noWrap textAlign="left" flexGrow={1}>
                {name}
              </Typography>
              {showNotConcludedAlert && (
                <ErrorIcon
                  sx={{
                    color: FORTA_ERROR,
                    width: width !== '100%' ? '11px' : '16px',
                    height: width !== '100%' ? '11px' : '16px',
                  }}
                />
              )}
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              noWrap
              textAlign="left"
              fontWeight={700}
              sx={{ visibility: showName ? 'visible' : 'hidden' }}
              lineHeight={reduceTextSize ? '14px' : '24px'}
              fontSize={reduceTextSize ? 12 : 14}
            >{`${client?.first_name} ${client?.last_name}`}</Typography>
            {showNotConcludedAlert && !showTypeLabel && (
              <ErrorIcon
                sx={{
                  color: FORTA_ERROR,
                  width: showName ? '14px' : '6px',
                  height: showName ? '14px' : '8px',
                  mt: showName ? 0 : -1,
                }}
              />
            )}
          </Box>

          {showTimeLabel && (
            <Typography textAlign="left" fontSize={14}>
              {getTimeLabel(resource)}
            </Typography>
          )}
        </Box>
      </Box>
    </FHTooltip>
  );
}

export default CalendarCard;
